import { workspacePermissions } from '@/core/ui/workspace'
import { customersWorkspace, organizationsWorkspace, salesDocumentsWorkspace, documentTemplatesWorkspace } from '@/modules/customers/crmWorkspaces'
import { executionWorkspace, externalWorkspace } from '@/modules/execution/executionWorkspaces'
import { procurementWorkspace, warehouseWorkspace } from '@/modules/procurement/procurementWorkspaces'
import { auditWorkspace, notificationSettingsWorkspace } from '@/modules/operations/systemWorkspaces'
import { administrationWorkspace, attendanceWorkspace, payrollWorkspace, requestsWorkspace } from '@/modules/workforce/workforceWorkspaces'
import { accountingCatalogsWorkspace, cashBankWorkspace, financeWorkspace, invoicesWorkspace, vouchersWorkspace } from '@/modules/finance/accountingWorkspaces'
import { workInboxWorkspace } from '@/modules/workflow/workInboxWorkspace'

export type PermissionMode = 'any' | 'all'

export interface UiMenuItem {
  labelKey: string
  icon?: string
  to?: string
  /** Query gắn vào liên kết, vd. mở đúng tab của màn dùng chung cho hai mục menu. */
  query?: Record<string, string>
  /** Giá trị query (theo khóa) để coi mục là đang chọn; mặc định là chính `query`. */
  activeQuery?: Record<string, string[]>
  items?: UiMenuItem[]
  permission?: string | string[]
  permissionMode?: PermissionMode
  activePaths?: string[]
  /** false: mục mở cho mọi người dùng (vd. Hộp công việc) không được chọn làm trang mở đầu thay cho màn nghiệp vụ theo vai trò. */
  landing?: false
}

export function canAccessMenuItem(item: UiMenuItem, can: (permission: string) => boolean) {
  if (!item.permission) return true
  const permissions = Array.isArray(item.permission) ? item.permission : [item.permission]
  return item.permissionMode === 'all' ? permissions.every(can) : permissions.some(can)
}

export function firstAuthorizedMenuPath(can: (permission: string) => boolean) {
  if (!can('ProjectDashboards.Read') && can('WbsNodes.Read')) return '/execution'
  const pending = [...applicationMenu]
  while (pending.length) {
    const item = pending.shift()!
    if (!canAccessMenuItem(item, can)) continue
    if (item.to && item.landing !== false) return item.to
    if (item.items) pending.unshift(...item.items)
  }
  return '/account'
}

// Menu theo phân hệ (docs/upgrade/03, giai đoạn C1): route giữ nguyên, chỉ đổi nhóm. Mua hàng và Kế toán cùng ProcurementService.
export const applicationMenu: UiMenuItem[] = [
  {
    labelKey: 'menu.overview',
    items: [
      { labelKey: 'nav.workInbox', icon: 'pi pi-inbox', to: '/work', permission: workspacePermissions(workInboxWorkspace), permissionMode: 'any', landing: false },
      { labelKey: 'nav.dashboard', icon: 'pi pi-chart-bar', to: '/', permission: 'ProjectDashboards.Read' },
    ]
  },
  {
    labelKey: 'menu.projectManagement',
    // ADR-108/ADR-109 (27/10/2026, điều chỉnh 2 của chủ sản phẩm): 3 mục — Dự án, Thực thi (ba tab Của tôi / Cảnh báo / Báo cáo dự án),
    // Chia sẻ ngoài (Dự án đứng trước để người chỉ có Projects.Read mở đầu ở sổ dự án; thành viên WbsNodes.Read mở đầu ở Thực thi). «Tổng quan» (Gantt 3 năm) là thẻ Hộp công việc + widget Trung tâm điều hành; «Bảng tiến độ», «Cập nhật theo dự án»,
    // «Vấn đề & đối sách» gộp vào chi tiết dự án; Bài học và Gửi định kỳ nằm trong tab Báo cáo dự án.
    items: [
      { labelKey: 'menu.projectRegister', icon: 'pi pi-file-edit', to: '/project-registration', permission: 'Projects.Read' },
      { labelKey: 'menu.projectExecution', icon: 'pi pi-check-square', to: '/execution', permission: workspacePermissions(executionWorkspace), permissionMode: 'any' },
      { labelKey: 'menu.projectExternal', icon: 'pi pi-share-alt', to: '/projects/external', permission: workspacePermissions(externalWorkspace) }
    ]
  },
  {
    labelKey: 'menu.crm',
    items: [
      { labelKey: 'nav.customers', icon: 'pi pi-building', to: '/customers', permission: workspacePermissions(customersWorkspace) },
      { labelKey: 'menu.organizations', icon: 'pi pi-sitemap', to: '/organizations', permission: workspacePermissions(organizationsWorkspace) },
      { labelKey: 'nav.products', icon: 'pi pi-box', to: '/products', permission: 'Products.Read' },
      { labelKey: 'menu.salesDocuments', icon: 'pi pi-file-check', to: '/sales-documents', permission: workspacePermissions(salesDocumentsWorkspace) },
      { labelKey: 'menu.documentTemplates', icon: 'pi pi-file-edit', to: '/document-templates', permission: workspacePermissions(documentTemplatesWorkspace) }
    ]
  },
  {
    labelKey: 'menu.purchasing',
    items: [
      { labelKey: 'menu.procurementDocuments', icon: 'pi pi-shopping-cart', to: '/procurement', permission: workspacePermissions(procurementWorkspace) },
      { labelKey: 'menu.warehouse', icon: 'pi pi-warehouse', to: '/warehouse', permission: workspacePermissions(warehouseWorkspace) },
      { labelKey: 'nav.materials', icon: 'pi pi-list-check', to: '/materials', permission: 'Materials.Read' }
    ]
  },
  {
    labelKey: 'menu.accounting',
    // docs/upgrade/06 (M1): 7 mục, mỗi mục một workspace nhiều tab; không còn nhóm con lồng.
    items: [
      { labelKey: 'menu.paymentVouchers', icon: 'pi pi-money-bill', to: '/vouchers', permission: workspacePermissions(vouchersWorkspace) },
      { labelKey: 'menu.invoices', icon: 'pi pi-receipt', to: '/invoices', permission: workspacePermissions(invoicesWorkspace) },
      { labelKey: 'menu.cashAndBank', icon: 'pi pi-book', to: '/cash-bank', permission: workspacePermissions(cashBankWorkspace) },
      { labelKey: 'menu.financeReports', icon: 'pi pi-chart-bar', to: '/finance', permission: workspacePermissions(financeWorkspace) },
      { labelKey: 'menu.accountingCatalogs', icon: 'pi pi-tags', to: '/accounting-catalogs', permission: workspacePermissions(accountingCatalogsWorkspace) }
    ]
  },
  {
    labelKey: 'menu.hrAdministration',
    // docs/upgrade/06 (M2): 6 mục; Chấm công chuyển từ Dự án vào "Chấm công & lịch" (Q2).
    items: [
      { labelKey: 'nav.employeeProfiles', icon: 'pi pi-address-book', to: '/employee-profiles', permission: ['EmployeeProfiles.Read'], permissionMode: 'any' },
      { labelKey: 'menu.requestsGroup', icon: 'pi pi-inbox', to: '/requests', permission: workspacePermissions(requestsWorkspace) },
      { labelKey: 'menu.attendanceGroup', icon: 'pi pi-clock', to: '/attendance', permission: workspacePermissions(attendanceWorkspace) },
      { labelKey: 'menu.payrollGroup', icon: 'pi pi-wallet', to: '/payroll', permission: workspacePermissions(payrollWorkspace) },
      { labelKey: 'menu.administration', icon: 'pi pi-car', to: '/administration', permission: workspacePermissions(administrationWorkspace) }
    ]
  },
  {
    labelKey: 'menu.system',
    // docs/upgrade/06 (M4): 6 mục.
    items: [
      { labelKey: 'menu.usersAndAccess', icon: 'pi pi-shield', to: '/identity-administration', query: { tab: 'users' }, activeQuery: { tab: ['users', 'roles', 'permissions', 'groups', 'departments'] }, permission: ['Users.Manage', 'Users.Read', 'Roles.Read', 'Departments.Read', 'Departments.Manage'], permissionMode: 'any' },
      { labelKey: 'nav.workflowDefinitions', icon: 'pi pi-sitemap', to: '/workflow-definitions', permission: 'WorkflowDefinitions.Read' },
      { labelKey: 'menu.notificationSettings', icon: 'pi pi-bell', to: '/notification-settings', permission: workspacePermissions(notificationSettingsWorkspace) },
      { labelKey: 'systemSettings.title', icon: 'pi pi-cog', to: '/system-settings', permission: 'SystemSettings.Read' },
      { labelKey: 'menu.audit', icon: 'pi pi-chart-line', to: '/audit', permission: workspacePermissions(auditWorkspace) },
      // Q5: Thùng rác dữ liệu là việc quản trị, chuyển từ CRM sang Hệ thống. Trợ giúp nằm trên topbar (F1).
      { labelKey: 'nav.crmTrash', icon: 'pi pi-trash', to: '/crm-trash', permission: ['Customers.Manage', 'Partners.Manage', 'Suppliers.Manage', 'Materials.Manage', 'Products.Manage'], permissionMode: 'any' }
    ]
  }
]
